import { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import { isEqual } from "lodash";
import { CustomSelect, FilterOptions, TermFilter } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/homeworks/HomeworksAction";
import { useHomeworkReportUIContext } from "../HomeworkReportUIContext";

const prepareFilter = (queryParams, values) => {
  let { term, teacherId, sectionId, courseId, createDate, createDateEnd, homeworkCount, evaluatedHomeworkCount, unevaluatedHomeworkCount, notYetDueHomeworkCount } = values;
  homeworkCount = homeworkCount === "" ? null : homeworkCount;
  evaluatedHomeworkCount = evaluatedHomeworkCount === "" ? null : evaluatedHomeworkCount;
  unevaluatedHomeworkCount = unevaluatedHomeworkCount === "" ? null : unevaluatedHomeworkCount;
  notYetDueHomeworkCount = notYetDueHomeworkCount === "" ? null : notYetDueHomeworkCount;
  const newQueryParams = { ...queryParams };
  const filter = { teacherId, sectionId, courseId, createDate, createDateEnd, homeworkCount, evaluatedHomeworkCount, unevaluatedHomeworkCount, notYetDueHomeworkCount };
  newQueryParams.filter = filter;
  newQueryParams.term = term;
  return newQueryParams;
};

export function HomeworkReportFilter() {
  // Homework Report UI Context
  const homeworkReportUIContext = useHomeworkReportUIContext();
  const homeworkReportUIProps = useMemo(() => {
    return {
      queryParams: homeworkReportUIContext.queryParams,
      setQueryParams: homeworkReportUIContext.setQueryParams
    };
  }, [homeworkReportUIContext]);

  const dispatch = useDispatch();
  const { currentActiveTerm, teachers, sections, courses } = useSelector(
    (state) => ({
      currentActiveTerm: state.auth.user.currentActiveTerm,
      teachers: state.homeworks.teachers,
      sections: state.homeworks.sections,
      courses: state.homeworks.courses
    }),
    shallowEqual
  );

  useEffect(() => {
    dispatch(actions.getAllList());
  }, [dispatch]);

  const applyFilter = (values) => {
    const newQueryParams = prepareFilter(homeworkReportUIProps.queryParams, values);
    if (!isEqual(newQueryParams, homeworkReportUIProps.queryParams)) {
      newQueryParams.pageNumber = 1;
      homeworkReportUIProps.setQueryParams(newQueryParams);
    }
  };

  const teacherOptions = FilterOptions.concat(teachers);
  const sectionOptions = FilterOptions.concat(sections);
  const courseOptions = FilterOptions.concat(courses);

  return (
    <Formik
      initialValues={{
        term: currentActiveTerm,
        createDate: "",
        createDateEnd: "",
        homeworkCount: "",
        evaluatedHomeworkCount: "",
        unevaluatedHomeworkCount: "",
        notYetDueHomeworkCount: ""
      }}
      onSubmit={(values) => {
        applyFilter(values);
      }}
    >
      {({
        values,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit} className="form form-label-right">
          <TermFilter
            handleSubmit={handleSubmit}
            handleBlur={handleBlur}
            handleChange={handleChange}
            onChange={e => dispatch(actions.getList(e.target.value))}
            values={values}
          />
          <div className="form-group row">
            <div className="col-sm-2"></div>
            <label className="form-label col-form-label col-sm-2">
              Tarih
            </label>
            <div className="col-lg-2">
              <input
                type="date"
                className="form-control"
                name="createDate"
                placeholder="Başlangıç"
                onBlur={handleBlur}
                value={values.createDate}
                onChange={(e) => {
                  setFieldValue("createDate", e.target.value);
                  handleSubmit();
                }}
              />
            </div>
            <div className="col-lg-2">
              <input
                type="date"
                className="form-control"
                name="createDateEnd"
                placeholder="Bitiş"
                onBlur={handleBlur}
                value={values.createDateEnd}
                onChange={(e) => {
                  setFieldValue("createDateEnd", e.target.value);
                  handleSubmit();
                }}
              />
            </div>
          </div>
          <div className="form-group row">
            <div className="col-sm-2"></div>
            <label className="form-label col-form-label col-sm-2">
              Sınıf
            </label>
            <div className="col-lg-4">
              <CustomSelect 
                options={sectionOptions}
                value={values.sectionId}
                placeholder="Sınıf"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("sectionId", e.value);
                  handleSubmit();
                }}
              />
            </div>
          </div>
          <div className="form-group row">
            <div className="col-lg-2">
              <CustomSelect
                options={teacherOptions}
                value={values.teacherId}
                placeholder="Öğretmen"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("teacherId", e.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Öğretmen</b>
              </small>
            </div>
            <div className="col-lg-2">
              <CustomSelect
                options={courseOptions}
                value={values.courseId}
                placeholder="Ders"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("courseId", e.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Ders</b>
              </small>
            </div>
            <div className="col-lg-2">
              <input
                type="number"
                className="form-control"
                name="homeworkCount"
                placeholder="Toplam Ödev Sayısı"
                onBlur={handleBlur}
                value={values.homeworkCount}
                onChange={(e) => {
                  setFieldValue("homeworkCount", e.target.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Toplam Ödev Sayısı</b>
              </small>
            </div>
            <div className="col-lg-2">
              <input
                type="number"
                className="form-control"
                name="evaluatedHomeworkCount"
                placeholder="Değerlendirilen ..."
                onBlur={handleBlur}
                value={values.evaluatedHomeworkCount}
                onChange={(e) => {
                  setFieldValue("evaluatedHomeworkCount", e.target.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Değerlendirilen Ödev Sayısı</b>
              </small>
            </div>
            <div className="col-lg-2">
              <input
                type="number"
                className="form-control"
                name="unevaluatedHomeworkCount"
                placeholder="Değerlendirilmeyen ..."
                onBlur={handleBlur}
                value={values.unevaluatedHomeworkCount}
                onChange={(e) => {
                  setFieldValue("unevaluatedHomeworkCount", e.target.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Değerlendirilmeyen Ödev Sayısı</b>
              </small>
            </div>
            <div className="col-lg-2">
              <input
                type="number"
                className="form-control"
                name="notYetDueHomeworkCount"
                placeholder="Değerlendirilme ..."
                onBlur={handleBlur}
                value={values.notYetDueHomeworkCount}
                onChange={(e) => {
                  setFieldValue("notYetDueHomeworkCount", e.target.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Değerlendirilme Tarihi Gelmeyen Ödev Sayısı</b>
              </small>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
}